import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Loader2, ShieldCheck, KeyRound, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const Field = ({ icon: Icon, label, ...props }) => (
  <label className="block">
    <span className="text-gray-400 text-sm">{label}</span>
    <div className="mt-1 flex items-center bg-gray-900 border border-gray-800 rounded-lg px-3">
      <Icon className="w-4 h-4 text-gray-500" />
      <input {...props} className="bg-transparent w-full px-2 py-2.5 text-white outline-none" />
    </div>
  </label>
);

const Shell = ({ subtitle, children, onSubmit }) => (
  <div className="min-h-screen bg-black flex items-center justify-center px-4" data-testid="admin-login-page">
    <form onSubmit={onSubmit} className="w-full max-w-sm bg-gray-950 border border-gray-800 rounded-2xl p-8 space-y-5">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-7 h-7 text-[#FF6B35]" />
        <div>
          <h1 className="text-white text-xl font-semibold">Yooin Admin</h1>
          <p className="text-gray-500 text-xs">{subtitle}</p>
        </div>
      </div>
      {children}
    </form>
  </div>
);

const Submit = ({ busy, label, testid }) => (
  <Button type="submit" disabled={busy} className="w-full bg-[#FF6B35] hover:bg-[#e55a2a]" data-testid={testid}>
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : label}
  </Button>
);

export default function AdminLogin() {
  const { login, enrollStart, enrollConfirm, verify, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [enroll, setEnroll] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = (fn) => async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const submitPassword = run(async () => {
    const res = await login(email, password);
    if (!res.success) { toast.error(res.error); return; }
    setPassword('');
    if (res.mfa === 'enroll') {
      try { setEnroll(await enrollStart()); setStep('enroll'); } catch { toast.error('No se pudo iniciar la configuración MFA'); }
    } else setStep('verify');
  });

  const submitEnroll = run(async () => {
    const res = await enrollConfirm(code.trim());
    if (!res.success) { toast.error(res.error); return; }
    setCode('');
    setRecovery(res);
    setStep('recovery');
  });

  const submitVerify = run(async () => {
    const res = await verify(useRecovery ? { recoveryCode: code.trim() } : { code: code.trim() });
    if (!res.success) { toast.error(res.error); return; }
    navigate('/', { replace: true });
  });

  if (step === 'enroll' && enroll) return (
    <Shell subtitle="Configura la verificación en dos pasos (obligatoria)" onSubmit={submitEnroll}>
      <p className="text-gray-300 text-sm">Escanea el código con Google Authenticator, Microsoft Authenticator, Authy o 1Password.</p>
      <img src={enroll.qr_svg_data_uri} alt="Código QR MFA" className="bg-white rounded-lg p-2 mx-auto w-48 h-48" data-testid="mfa-qr" />
      <div className="text-xs text-gray-500 break-all">
        Clave manual: <span className="text-gray-300 font-mono" data-testid="mfa-manual-secret">{enroll.manual_secret}</span>
      </div>
      <Field icon={KeyRound} label="Código de 6 dígitos" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
        required value={code} onChange={(e) => setCode(e.target.value)} data-testid="mfa-enroll-code" />
      <Submit busy={busy} label="Activar MFA" testid="mfa-enroll-submit" />
    </Shell>
  );

  if (step === 'recovery' && recovery) return (
    <Shell subtitle="Guarda tus códigos de recuperación" onSubmit={(e) => { e.preventDefault(); setUser(recovery.user); navigate('/', { replace: true }); }}>
      <p className="text-gray-300 text-sm">Cada código sirve una sola vez si pierdes tu dispositivo. No volverán a mostrarse.</p>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm text-white bg-gray-900 rounded-lg p-3" data-testid="mfa-recovery-codes">
        {recovery.recoveryCodes.map((c) => <span key={c}>{c}</span>)}
      </div>
      <Button type="button" variant="outline" className="w-full" data-testid="mfa-recovery-copy"
        onClick={() => { navigator.clipboard?.writeText(recovery.recoveryCodes.join('\n')); toast.success('Códigos copiados'); }}>
        <Copy className="w-4 h-4 mr-1" /> Copiar códigos
      </Button>
      <Submit busy={false} label="Ya los guardé, continuar" testid="mfa-recovery-continue" />
    </Shell>
  );

  if (step === 'verify') return (
    <Shell subtitle="Verificación en dos pasos" onSubmit={submitVerify}>
      <Field icon={KeyRound} label={useRecovery ? 'Código de recuperación' : 'Código de tu app autenticadora'}
        inputMode={useRecovery ? 'text' : 'numeric'} autoComplete="one-time-code" required value={code}
        onChange={(e) => setCode(e.target.value)} data-testid="mfa-verify-code" />
      <Submit busy={busy} label="Verificar" testid="mfa-verify-submit" />
      <button type="button" className="text-xs text-gray-400 hover:text-white" data-testid="mfa-toggle-recovery"
        onClick={() => { setUseRecovery((v) => !v); setCode(''); }}>
        {useRecovery ? 'Usar código de la app' : 'Usar un código de recuperación'}
      </button>
    </Shell>
  );

  return (
    <Shell subtitle="Acceso exclusivo para administradores" onSubmit={submitPassword}>
      <Field icon={Mail} label="Correo" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        autoComplete="username" data-testid="admin-login-email" />
      <Field icon={Lock} label="Contraseña" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password" data-testid="admin-login-password" />
      <Submit busy={busy} label="Ingresar" testid="admin-login-submit" />
    </Shell>
  );
}
